import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, Clock, Target } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { FAQSection } from '../components/FAQSection';
import { trackPageView } from '../utils/analytics';

export const WebsiteSpeedTest = () => {
  useEffect(() => {
    trackPageView('/website-speed-test');
  }, []);

  const faqs = [
    {
      question: "What is a website speed test?",
      answer: "A website speed test measures how quickly your web pages load and become interactive. It analyses key performance metrics including load time, time to first byte (TTFB), and Core Web Vitals such as LCP, INP, and CLS. Web Speed Advisor also turns those findings into prioritised recommendations so you know what to fix first and where to apply each change."
    },
    {
      question: "Why is website speed important for SEO?",
      answer: "Google's ranking systems use Core Web Vitals and other page experience signals, especially when there are multiple relevant results for a query. A faster site does not guarantee higher rankings, but it can improve user experience, reduce abandonment, and remove performance issues that hold strong content back."
    },
    {
      question: "How often should I run a page speed test?",
      answer: "Run a page speed test after every significant deployment — new features, plugin updates, image batch uploads, or hosting migrations. For production sites, a monthly baseline test is a sensible minimum. Continuous monitoring catches performance regressions before they affect rankings or user experience."
    },
    {
      question: "What is a good Lighthouse score?",
      answer: "Google Lighthouse scores range from 0 to 100. A score of 90 or above is considered good, 50–89 needs improvement, and below 50 is poor. The Lighthouse score is a useful lab indicator, but focus equally on real-user metrics — LCP under 2.5 s, INP under 200 ms, and CLS below 0.1 — because those reflect the page experience Google recommends and users actually feel."
    },
    {
      question: "Can I test mobile and desktop performance separately?",
      answer: "Yes. Web Speed Advisor runs your page speed test for both mobile and desktop in a single analysis. Mobile and desktop results frequently differ due to network throttling, device CPU constraints, and viewport rendering. Reviewing both ensures you optimise for every segment of your audience."
    },
    {
      question: "Can this tool help me implement fixes?",
      answer: "Yes. Web Speed Advisor provides implementation-oriented snippets for recommendations, and the current implementation beta can prepare draft GitHub pull requests for a narrow set of low-risk frontend fixes."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Website Speed Test — Free Page Speed & Lighthouse Tool | Web Speed Advisor"
        description="Run a free website speed test. Check your Lighthouse score, Core Web Vitals, and mobile and desktop performance in one report with AI-powered fixes."
        canonical="https://webspeedadvisor.com/website-speed-test"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Web Speed Advisor Website Speed Test',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'Web',
          url: 'https://webspeedadvisor.com/website-speed-test',
          description: 'Free website speed test for Lighthouse performance, Core Web Vitals, mobile and desktop analysis, and AI-powered optimisation recommendations.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          }
        }}
      />

      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-black font-chivo mb-6">
            Free Website Speed Test Tool
          </h1>
          <p className="text-xl text-slate-300 mb-8">
            Run a page speed test for any URL and get your Lighthouse score, Core Web Vitals, and prioritised AI recommendations — covering both mobile and desktop performance in one report.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Run Free Website Speed Test
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-12">
        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Why Test Your Website Speed?
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700">
            <p>
              Page speed affects three things that matter to every website owner: search visibility, user experience, and revenue. Google uses Core Web Vitals in its ranking systems, and fast pages give visitors a better experience once they land on your site.
            </p>
            <p>
              Running a regular website speed test gives you a measurable baseline. You can identify which pages are underperforming, confirm that optimisations are working, and catch regressions before they affect visitors or organic performance. A faster page can improve conversions, reduce bounce rate, and make important content easier to crawl and render.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            What This Page Speed Test Measures
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-blue-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Performance Score</h3>
                <p className="text-sm text-slate-600">
                  Your overall Lighthouse score from 0 to 100, based on Google's PageSpeed Insights audit. It reflects load time, resource optimisation, and rendering efficiency.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Clock className="text-green-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Page Experience Signals</h3>
                <p className="text-sm text-slate-600">
                  Core Web Vitals — Largest Contentful Paint (LCP), Interaction to Next Paint (INP), and Cumulative Layout Shift (CLS) — measured against Google's passing thresholds.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="text-purple-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">AI Recommendations</h3>
                <p className="text-sm text-slate-600">
                  Prioritised, plain-English recommendations that translate raw audit data into actionable guidance — including the issue, the likely cause, and the implementation steps.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Zap className="text-orange-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Mobile &amp; Desktop</h3>
                <p className="text-sm text-slate-600">
                  Separate mobile and desktop performance scores in a single speed test run, so you can optimise for every device type your visitors use.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Understanding Your Speed Test Results
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              Your speed test report includes a Lighthouse performance score, individual Core Web Vitals readings, and a breakdown of specific audit failures. Here is what each metric means:
            </p>
            <h3 className="text-2xl font-bold text-slate-900 mt-8">Lighthouse Performance Score (0–100)</h3>
            <p>
              This is your overall grade from Google's Lighthouse audit. A score of 90–100 is good, 50–89 needs improvement, and 0–49 is poor. The score is a weighted average of several timing metrics and best-practice checks. Use it as a directional indicator, not an absolute target.
            </p>
            <h3 className="text-2xl font-bold text-slate-900 mt-8">Core Web Vitals</h3>
            <p>
              Core Web Vitals are Google's user-centric metrics. They measure real-world experience and are used by Google's ranking systems as part of broader page experience evaluation:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Largest Contentful Paint (LCP):</strong> Measures loading performance — specifically how long it takes for the largest visible element to render. A good LCP is 2.5 seconds or faster.
              </li>
              <li>
                <strong>Interaction to Next Paint (INP):</strong> Measures responsiveness across all interactions during a page visit. A good INP is under 200 milliseconds. High INP typically points to heavy JavaScript execution on the main thread.
              </li>
              <li>
                <strong>Cumulative Layout Shift (CLS):</strong> Measures visual stability — how much page elements shift unexpectedly as the page loads. A CLS below 0.1 is considered good.
              </li>
            </ul>
          </div>
        </section>

        <section className="bg-slate-100 rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Why AI Recommendations Matter
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700">
            <p>
              Raw Lighthouse audit output lists issues but does not rank them by impact, explain the root cause, or tell you who on your team should handle the fix. A long list of warnings can be harder to act on than no list at all.
            </p>
            <p>
              Web Speed Advisor's AI layer reviews the full audit, identifies the issues with the strongest likely impact on your Lighthouse score and Core Web Vitals, and rewrites them as contextual recommendations in plain English. Each recommendation explains the problem, the probable cause, the expected benefit of fixing it, and the concrete implementation steps.
            </p>
            <p>
              The result is a speed test report that non-technical stakeholders can read, developers can act on immediately, and teams can use to prioritise sprint work — without needing to interpret raw performance data themselves.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Common Speed Issues and Quick Fixes
          </h2>
          <div className="bg-white rounded-xl p-8 shadow-sm space-y-6">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">1. Unoptimised Images</h3>
              <p className="text-slate-700">
                Oversized or uncompressed images are one of the most common causes of a poor Lighthouse score. <strong>Fix:</strong> Compress images, convert to WebP or AVIF, and apply lazy loading to any image below the fold.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">2. Render-Blocking Resources</h3>
              <p className="text-slate-700">
                CSS and JavaScript files that block the critical rendering path delay LCP and increase Time to Interactive. <strong>Fix:</strong> Inline critical CSS, defer non-essential JavaScript, and remove unused stylesheets.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">3. Slow Server Response Time (TTFB)</h3>
              <p className="text-slate-700">
                A high Time to First Byte pushes every subsequent metric later. <strong>Fix:</strong> Use a content delivery network (CDN), enable server-side caching, optimise database queries, and review your hosting tier.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">4. Missing or Misconfigured Caching</h3>
              <p className="text-slate-700">
                Without effective caching, browsers re-download static assets on every visit. <strong>Fix:</strong> Set long-lived cache-control headers for static resources, use a CDN for edge caching, and consider a service worker for repeat visitors.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            How to Improve Your Website Speed
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700">
            <p>
              Start by running a page speed test to establish your baseline Lighthouse score and Core Web Vitals. Then work through the following in order of impact:
            </p>
            <ol className="list-decimal pl-6 space-y-2 mt-4">
              <li>
                <strong>Optimise images:</strong> Compress all images, convert to WebP or AVIF, and use <code>srcset</code> to serve appropriately sized images to each viewport.
              </li>
              <li>
                <strong>Reduce HTTP requests:</strong> Consolidate files where possible, remove unused plugins and scripts, and eliminate any third-party tags that are not essential.
              </li>
              <li>
                <strong>Enable compression:</strong> Apply Gzip or Brotli compression to HTML, CSS, and JavaScript files. This typically reduces transfer size by 60–80 %.
              </li>
              <li>
                <strong>Configure browser caching:</strong> Set cache-control headers with long max-age values for static assets so returning visitors load pages from their local cache.
              </li>
              <li>
                <strong>Minify CSS, JavaScript, and HTML:</strong> Remove comments, whitespace, and redundant characters to reduce file sizes without changing behaviour.
              </li>
              <li>
                <strong>Use a CDN:</strong> Serve assets from edge nodes close to your visitors to reduce latency and improve mobile and desktop performance globally.
              </li>
              <li>
                <strong>Optimise the critical rendering path:</strong> Inline critical CSS, preload key resources, and defer any JavaScript that is not needed for initial render.
              </li>
            </ol>
          </div>
        </section>

        <FAQSection faqs={faqs} />

        <section className="bg-primary text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold font-chivo mb-4">
            Ready to Test Your Website Speed?
          </h2>
          <p className="text-lg mb-6 text-blue-100">
            Get your Lighthouse score, Core Web Vitals, and AI-powered recommendations for mobile and desktop in under a minute.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Start Free Speed Test
          </Link>
        </section>
      </div>
    </div>
  );
};

export default WebsiteSpeedTest;
